import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import {  format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { IoCalendarOutline, IoRefreshOutline } from 'react-icons/io5';
import { showToastMessage } from 'src/coach/service/coach.utils';
import { useModal } from 'src/shared/hooks/use-modal';
import BlockLoading from 'src/shared/ui/components/block-loading/block-loading';
import AddEditTimeOffModal from './addTimeOff/addTimeOff';
import { ScheduledTimeOffList } from './components/time-off-list';
import { deleteScheduledTimeOffAsyncMutation } from './graphql/scheduled-time-off-mutation';
import { getScheduledTimeOffs } from './graphql/scheduled-time-off-query';
import { useAsyncTaskStatus } from '../class-details/hooks/async-task-status';
import { useLoading } from 'src/shared/hooks/use-loading';

const ScheduledTimeOff = ({data}) => {
  const today = new Date();
  const firstDateOfMonth = format(today, 'yyyy-MM-01');
  const {loading:loadingState} = useLoading();

  const [startDate, setStartDate] = useState(firstDateOfMonth);
  const [endDate, setEndDate] = useState(null);


  // const [endingAfter,setEndingAfter] = useState((new Date()),);
  const coachId = data?.account?.id;
  const { isOpen:isOpenUpsert, actions, context:selectedItem } = useModal();

const {setAsyncTaskId} = useAsyncTaskStatus({
  onComplete: async() => {
    scheduledTimeOffRefetch();
    loadingState.stop();
    showToastMessage('Scheduled time off created successfully');
    },
  onFail: () => {
      loadingState.stop();
      showToastMessage('Could not remove Scheduled Time Off','error');
    },
    poolingInterval: 1000,
  })

  const [deleteTimeOff] = useMutation(deleteScheduledTimeOffAsyncMutation);

  const {
    data: scheduledTimeOffData,
    previousData: previousScheduledTimeOffData,
    loading:scheduledTimeOffLoading,
    networkStatus,
    refetch: scheduledTimeOffRefetch,
  } = useQuery(getScheduledTimeOffs, {
    fetchPolicy:'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        coach: [Number(coachId)],
        // endingAfter,
        startDate,
        endDate,
      },
    },
  });
  const scheduledTimeOffs = scheduledTimeOffData?.scheduledTimeOffFranchise || previousScheduledTimeOffData?.scheduledTimeOffFranchise;

  const coachFranchises = useMemo(()=>{
    const franchises = scheduledTimeOffs?.filter(f=>Boolean(f.franchiseId))?.map(item=>item.franchise);
    return franchises;
  },[scheduledTimeOffs]);

  const noTimeOffs = scheduledTimeOffs?.every(item=>item?.timeOffs?.length === 0);
  const handleEdit = (row) => {
    actions.open(row);
  };

  const handleDelete = async (id) => {
    try {
      loadingState.start();
      const response = await deleteTimeOff({
        variables: {
          input: id,
        },
      });

      if (response.data?.deleteScheduledTimeOffAsync?.id) {
        setAsyncTaskId(response.data.deleteScheduledTimeOffAsync.id);
      } else {
        loadingState.stop();
        showToastMessage('Could not remove Scheduled Time Off','error');
      }
    } catch {
      loadingState.stop();
      showToastMessage('Could not remove Scheduled Time Off','error');
    }
  };


  return (
    <Box className="tw-pb-24">
      <div className='tw-mb-4 tw-flex tw-flex-col tw-bg-white lg:tw-flex-row lg:tw-justify-between lg:tw-items-center tw-gap-4 tw-sticky tw-top-[60px] tw-z-[1]'>
      <div>
       <span className="tw-text-sm">Request Dates</span>
       <div className="tw-flex tw-gap-4 sm:tw-gap-2 tw-items-center">
        <div className='tw-w-32 sm:tw-w-auto'>
        <DatePicker
        selected={startDate}
        onChange={(date) => setStartDate(format(date, 'yyyy-MM-dd'))}
        popperClassName={'tw-ml-10'}                
        customInput={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '16px',
              width: '140px',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={(e) => e.preventDefault()}
          >
            <input
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '16px',
                width: '100%',
                padding: 0,
                margin: 0,
                outline: 'none',
              }}
              value={startDate ? startDate: ''}
              readOnly
            />
            <IoCalendarOutline style={{ fontSize: '18px', color: '#000' }} />
          </div>
        }
      />
        </div>
        <div className="tw-text-xs sm:tw-text-base tw-hidden sm:tw-block">To</div>
        <div className='tw-w-32 sm:tw-w-auto'>
        <DatePicker
        selected={endDate}
        onChange={(date) => setEndDate(format(date, 'yyyy-MM-dd'))}
        customInput={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #ddd',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '16px',
              width: '140px',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={(e) => e.preventDefault()}
          >
            <input
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '16px',
                width: '100%',
                padding: 0,
                margin: 0,
                outline: 'none',
              }}
              value={endDate ? endDate: ''}
              readOnly
            />
            <IoCalendarOutline style={{ fontSize: '18px', color: '#000' }} />
          </div>
        }
      />
          
        </div>
        <IconButton
                color="inherit"
                onClick={() => {
                  setStartDate(firstDateOfMonth);
                  setEndDate(null);
                  scheduledTimeOffRefetch();
                }}
              >
                <IoRefreshOutline
                  className="cursor-pointer"
                  color="#06b0f0"
                  fontSize={'24px'}
                />
              </IconButton>
      </div>
      </div>
        <Button className={!scheduledTimeOffLoading?'tw-bg-blue-light': 'tw-bg-gray'} disabled={ scheduledTimeOffLoading || networkStatus === NetworkStatus.loading}  onClick={actions.open} variant="contained" startIcon={<AddIcon />}>Request Time Off</Button>
      </div>
       
      {
        <BlockLoading loading={ scheduledTimeOffLoading || networkStatus === NetworkStatus.loading}>
      {noTimeOffs ? (
        <p className="tw-text-center tw-text-lg tw-mt-4">No Scheduled Time off found</p>
      ) : (
        scheduledTimeOffs?.map((item) => {
          if (!item?.timeOffs?.length) return null;
          return (
            <div key={item.franchiseId} className='tw-my-2'>
              <Typography className="tw-font-bold tw-text-[20px] tx-text-black">{item?.franchise?.name}</Typography>
              <ScheduledTimeOffList
              loading={loadingState.get}
              franchise = {item.franchise}
              timeOffs = {item.timeOffs}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              refetch={scheduledTimeOffRefetch}
              />
            </div>
          );
        })
      )}
    </BlockLoading>
      }
      

      {isOpenUpsert&&
        <AddEditTimeOffModal
          refetch={scheduledTimeOffRefetch}
          franchises={coachFranchises}
          coachId={coachId}
          open={isOpenUpsert}
          handleClose={actions.close}
          initialData={selectedItem} 
          handleSave={()=>{}} 
        />
}
    </Box>
  );
};

export default ScheduledTimeOff;
